// Brain state (§10.1, §22): everything one bot remembers between ticks. `BotBrain` (botTypes.ts)
// is the frozen surface `{ cfg, rng }`; this record extends it with the goal, the motor's stick
// state, per-state plans (teeter, ledge, mash), item and social timers and cross-round memory
// (grudges). Created by `createBrain`, cleared per round by `resetBrainForRound`.
import { NO_SLOT } from '../constants';
import { chance, rand, randRange, seedRng } from '../rng';
import type { BotBrain, BotConfig } from './botTypes';
import { resolveParams, type BotParams } from './params';
import type { Leg } from './routes';
import { BOT_TUNE } from './tuning';

/** Goals (§10.2, §10.4). Order is append-only: the harness and the debug viewer print these. */
export const Goal = {
  WANDER: 0, ATTACK: 1, EVADE: 2, RECOVER: 3, ITEM: 4,
  POTATO_PASS: 5, POTATO_FLEE: 6, CROWN_FETCH: 7, CROWN_HOLD: 8,
  HAUNT: 9, LOBBY: 10, DANCE: 11, PODIUM: 12, CUP: 13, BOMB_GRAB: 14,
} as const;
export type Goal = (typeof Goal)[keyof typeof Goal];

export const GOAL_NAMES: Readonly<Record<number, string>> = Object.fromEntries(Object.entries(Goal).map(([k, v]) => [v, k]));

/** Goals that claim a bean target and count toward the max-2-bots-per-target rule. */
export function goalTargetsBean(goal: Goal): boolean {
  return goal === Goal.ATTACK || goal === Goal.POTATO_PASS || goal === Goal.PODIUM;
}

/** What the motor should do this tick (filled by the tactic, consumed by the motor). */
export interface Want {
  /** 0 stop, 1 go to point (px, py), 2 move along direction (px, py). */
  mode: 0 | 1 | 2;
  px: number; py: number;
  /** Stick magnitude 0..1. */
  speed: number;
  /** Minimum safety the motor should keep (meters). */
  margin: number;
  /** Slow down within this distance of the point. */
  arrive: number;
  /** Reflex movement: snap the stick (little smoothing), no wander wobble. */
  urgent: boolean;
  /** Precise positioning (no wobble, no idle shuffle). */
  precise: boolean;
  /** Bean the motor may run into (target of a ram or a stomp); NO_SLOT otherwise. */
  contact: number;
  /** Ignore hazard danger (escaping one already). */
  ignoreDanger: boolean;
  /** Ignore bystanders (a hazard escape goes through, not around). */
  noCrowd: boolean;
}

/** One pending button press (the motor applies aim noise and blunders to shoves). */
export interface Press {
  shove: boolean; grab: boolean; jump: boolean; item: boolean;
  /** Attack direction for shove/grab (unnormalized; 0,0 = facing). */
  sx: number; sy: number;
  /** Aim for item/throw (meters from self; 0,0 = facing). */
  ax: number; ay: number;
  /** Apply aim noise and the blunder roll to this shove. */
  noisy: boolean;
}

export interface Brain extends BotBrain {
  p: BotParams;
  /** Stagger phase for thinking (0..thinkEvery−1). */
  phase: number;
  // ---- wrapping press counters (§20.3) and held-bit timers ----
  cShove: number; cGrab: number; cJump: number; cItem: number; cHonk: number; cEmote: number;
  emote: number;
  heldShove: number; heldGrab: number; heldJump: number; heldItem: number;
  // ---- goal ----
  goal: Goal;
  target: number;
  /** Entity id for ITEM / CROWN_FETCH / BOMB_GRAB goals (0 none). */
  targetEnt: number;
  goalSince: number;
  goalU: number;
  /** Point for WANDER / EVADE / POTATO_FLEE / CROWN_HOLD / LOBBY / DANCE goals. */
  gx: number; gy: number;
  pointTick: number;
  /** Next think is forced (target vanished, state changed). */
  forceThink: boolean;
  lastThink: number;
  // ---- attack sub-state ----
  commitAt: number;
  grabRoll: number;
  grabRollTick: number;
  baitUntil: number;
  circleSide: number;
  lastShoveTick: number;
  lastHitTick: number;
  // ---- reflexes ----
  /** Sidestep until this tick along (evx, evy). */
  evadeUntil: number;
  evx: number; evy: number;
  /** Lunge/windup start tick already reacted to, per bean. */
  reacted: Int32Array;
  /** Planned hazard jump: jump at this tick (−1 none); `hazardNoise` is this approach's timing error. */
  hazardNoise: number;
  hazardNoiseTick: number;
  recovering: boolean;
  // ---- per-state plans ----
  /** Bean state seen last tick, the one before it, and the tick the current state began. */
  lastState: number;
  prevState: number;
  stateTick: number;
  teeterSave: boolean;
  teeterHop: boolean;
  teeterAt: number;
  ledgeClimbAt: number;
  ledgeMindGame: boolean;
  /** The ledge climb is waiting for an approaching stomper (mind game in progress). */
  ledgeWaiting: boolean;
  /** Ticks this hang has watched a stomper come closer. */
  stomperTicks: number;
  /** This hang waits out an enemy lurking over the climb-out spot instead of climbing into its shove. */
  ledgePatient: boolean;
  /** Ghost: tick the pebble last became ready (impatient drops). */
  ghostReadySince: number;
  /** Ghost pebble aim error (meters) and when it is re-rolled. */
  ghostAimX: number;
  ghostAimY: number;
  ghostAimUntil: number;
  /** Island route cache (Cake travel): when planned, toward where, and the first leg. */
  routeTick: number;
  routeGoalX: number;
  routeGoalY: number;
  routeOk: boolean;
  routeLeg: Leg;
  /** Map anchor tile this bot heads for or holds (Cake), −1 none. */
  anchorTile: number;
  /** Pebble already judged (dodge or not), by entity id, and the verdict. */
  pebbleSeen: number;
  pebbleDodge: boolean;
  /** Bomb that `bombNoticed` was rolled for (the notice roll is once per bomb, like pebbles). */
  bombSeen: number;
  bombNoticed: boolean;
  mashNext: number;
  mashFlip: number;
  mashCount: number;
  diSide: number;
  carryTick: number;
  // ---- motor ----
  sx: number; sy: number;
  dirIdx: number;
  wobblePhase: number;
  wobbleRate: number;
  shuffleUntil: number;
  shuffleNext: number;
  shuffleX: number; shuffleY: number;
  /** True while the bot deliberately stands still (ledge wait, ready pad, dance pose). */
  waiting: boolean;
  want: Want;
  press: Press;
  // ---- items ----
  itemNext: number;
  /** The item last seen in hand and since when (item boredom). */
  itemKind: number;
  itemSince: number;
  bananaNext: number;
  // ---- social ----
  honkAt: number;
  honkNext: number;
  lastHonk: number;
  emoteAt: number;
  emoteId: number;
  danceRolled: boolean;
  danceUntil: number;
  brawlMode: boolean;
  brawlUntil: number;
  /** Bean that last KO'd this bot (§10.2 grudge) and the round tick the grudge ends. */
  grudge: number;
  grudgeUntil: number;
  /** Round id the grudge was earned in (the grudge lasts the rest of it plus 30 s of the next). */
  grudgeRound: number;
  // ---- mode memory ----
  lobbyTask: number;
  lobbyUntil: number;
  /** Lunge started by this bot last (telemetry: clash attempts, blunders). */
  lastLungeKind: number;
  // ---- added by the manager (WP4 orchestration) ----
  /** Bean that last hit this bot and when (retaliation interest). */
  lastHitBy: number;
  lastHitByTick: number;
  /** Earliest round tick for Gary's next teacup ride. */
  cupNext: number;
  /** ATTACK: tick the hover ends without an opening (−1: roll on the next attack tick). */
  patienceUntil: number;
  /** Tick of the last bubble-hop roll. */
  bubbleRollTick: number;
  /** ATTACK: this approach's range misjudgment (meters; > 0 lunges early). */
  rangeErr: number;
  /** Per bean: round tick until which it counts as out of reach (Cake islands). */
  unreachableUntil: Int32Array;
  /** Crown Grab: the slot that last lost the crown (pop event). */
  crownPoppedBy: number;
  /** Crown Grab: no commit on a fresh wearer before this tick (re-orienting after a pickup). */
  crownHesitateUntil: number;
  /** Crown Grab: round tick this bot last had the crown knocked off (it wants it back). */
  crownLostTick: number;
  /** Crown Grab: round tick the current wearer put the crown on (a fresh wearer in a crowd gets mobbed). */
  crownPickTick: number;
  /** Spin Cycle: last tick the bot steered for a spot in the gap between the arms. */
  gapRideTick: number;
  /** Tick the bot notices the hazard danger at its feet (−1: none pending). */
  dangerSince: number;
  /** A hazard reflex owns the stick this tick (RECOVER only overrides it in a real emergency). */
  hazardOwned: boolean;
  /** Sweeper escape: steer against the arm (rotation sign `armEscapeDir`) until this tick. */
  armEscapeUntil: number;
  armEscapeDir: number;
  /** Motor anti-stuck: ticks pushing without moving, and the detour in progress. */
  stuckTicks: number;
  unstickUntil: number;
  unstickX: number;
  unstickY: number;
  /** Counters were synced to the bean's last decoded presses (no phantom press on takeover). */
  synced: boolean;
  /** Edge greed (a point-blank shove at the rim that will teeter): the current roll and when it was made. */
  greedy: boolean;
  greedTick: number;
  /** Ticks the honk reply is pending for, and whether the last honk was itself a reply. */
  replyHonk: boolean;
  /** Round tick until which honk replies are suppressed. */
  honkQuietUntil: number;
}

function want(): Want {
  return { mode: 0, px: 0, py: 0, speed: 0, margin: 0, arrive: 0.6, urgent: false, precise: false, contact: NO_SLOT, ignoreDanger: false, noCrowd: false };
}

function press(): Press {
  return { shove: false, grab: false, jump: false, item: false, sx: 0, sy: 0, ax: 0, ay: 0, noisy: false };
}

export function createBrain(cfg: BotConfig, thinkEvery: number): Brain {
  const holder = { rng: seedRng(cfg.seed) };
  const p = resolveParams(cfg, holder);
  const b: Brain = {
    cfg, rng: holder.rng, p,
    phase: ((cfg.slot % thinkEvery) + thinkEvery) % thinkEvery,
    cShove: 0, cGrab: 0, cJump: 0, cItem: 0, cHonk: 0, cEmote: 0, emote: 0,
    heldShove: 0, heldGrab: 0, heldJump: 0, heldItem: 0,
    goal: Goal.WANDER, target: NO_SLOT, targetEnt: 0, goalSince: -1e9, goalU: 0,
    gx: 0, gy: 0, pointTick: -1e9, forceThink: true, lastThink: -1e9,
    commitAt: -1, grabRoll: 1, grabRollTick: -1e9, baitUntil: -1, circleSide: 1, lastShoveTick: -1e9, lastHitTick: -1e9,
    evadeUntil: -1, evx: 0, evy: 0, reacted: new Int32Array(8).fill(-1e9), hazardNoise: 0, hazardNoiseTick: -1e9, recovering: false,
    lastState: -1, prevState: -1, stateTick: 0, teeterSave: true, teeterHop: false, teeterAt: 0, ledgeClimbAt: -1, ledgeMindGame: false, ledgeWaiting: false, stomperTicks: 0, ledgePatient: false, ghostReadySince: 0, ghostAimX: 0, ghostAimY: 0, ghostAimUntil: 0, routeTick: -1e9, routeGoalX: 0, routeGoalY: 0, routeOk: false, routeLeg: { x: 0, y: 0, hop: false }, anchorTile: -1, pebbleSeen: 0, pebbleDodge: false, bombSeen: 0, bombNoticed: false,
    mashNext: 0, mashFlip: 1, mashCount: 0, diSide: 0, carryTick: 0,
    sx: 0, sy: 0, dirIdx: -1, wobblePhase: 0, wobbleRate: 0, shuffleUntil: -1, shuffleNext: 0, shuffleX: 0, shuffleY: 0,
    waiting: false, want: want(), press: press(),
    itemNext: 0, itemKind: 0, itemSince: 0, bananaNext: 0,
    honkAt: -1, honkNext: 0, lastHonk: -1e9, emoteAt: -1, emoteId: 0, danceRolled: false, danceUntil: -1,
    brawlMode: false, brawlUntil: -1,
    grudge: NO_SLOT, grudgeUntil: -1e9, grudgeRound: -1,
    lobbyTask: 0, lobbyUntil: -1, lastLungeKind: 0,
    lastHitBy: NO_SLOT, lastHitByTick: -1e9, cupNext: 0, patienceUntil: -1, bubbleRollTick: -1e9, rangeErr: 0, stuckTicks: 0, unstickUntil: -1, unreachableUntil: new Int32Array(8), crownPoppedBy: NO_SLOT, crownHesitateUntil: -1, crownLostTick: -1e9, crownPickTick: -1e9, gapRideTick: -1e9, dangerSince: -1, hazardOwned: false, armEscapeUntil: -1, armEscapeDir: 1, unstickX: 0, unstickY: 0, synced: false, replyHonk: false, honkQuietUntil: -1,
    greedy: false, greedTick: -1e9,
  };
  b.wobbleRate = randRange(holder, BOT_TUNE.motor.wobbleRateMin, BOT_TUNE.motor.wobbleRateMax);
  b.wobblePhase = rand(holder) * Math.PI * 2;
  b.circleSide = chance(holder, 0.5) ? 1 : -1;
  b.rng = holder.rng;
  return b;
}

/** Clears everything that belongs to one round; keeps identity, press counters and grudges. */
export function resetBrainForRound(b: Brain): void {
  b.goal = Goal.WANDER;
  b.target = NO_SLOT;
  b.targetEnt = 0;
  b.goalSince = -1e9;
  b.goalU = 0;
  b.pointTick = -1e9;
  b.forceThink = true;
  b.lastThink = -1e9;
  b.commitAt = -1;
  b.grabRollTick = -1e9;
  b.baitUntil = -1;
  b.lastShoveTick = -1e9;
  b.lastHitTick = -1e9;
  b.evadeUntil = -1;
  b.reacted.fill(-1e9);
  b.hazardNoiseTick = -1e9;
  b.recovering = false;
  b.lastState = -1;
  b.prevState = -1;
  b.ledgeClimbAt = -1;
  b.ledgeWaiting = false;
  b.ledgePatient = false;
  b.carryTick = 0;
  b.sx = b.sy = 0;
  b.dirIdx = -1;
  b.shuffleUntil = -1;
  b.shuffleNext = 0;
  b.waiting = false;
  b.itemNext = 0;
  b.bananaNext = 0;
  b.honkAt = -1;
  b.emoteAt = -1;
  b.danceRolled = false;
  b.danceUntil = -1;
  b.brawlUntil = -1;
  b.brawlMode = false;
  b.lobbyUntil = -1;
  b.lastHitBy = NO_SLOT;
  b.lastHitByTick = -1e9;
  b.cupNext = 0;
  b.patienceUntil = -1;
  b.stuckTicks = 0;
  b.unstickUntil = -1;
  b.armEscapeUntil = -1;
  b.anchorTile = -1;
  b.routeTick = -1e9;
  b.unreachableUntil.fill(0);
  b.honkQuietUntil = -1;
  b.replyHonk = false;
  b.greedTick = -1e9;
  b.crownLostTick = -1e9;
  b.crownPickTick = -1e9;
  b.gapRideTick = -1e9;
  b.crownHesitateUntil = -1;
  b.crownPoppedBy = NO_SLOT;
}
